import { Textarea } from '@chakra-ui/react'
import { mdiChevronDown, mdiChevronUp } from '@mdi/js'
import Icon from '@mdi/react'
import { DateTimeFormatOptions } from 'luxon'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Box } from '@/components/ui/box'
import { Flex } from '@/components/ui/flex'
import { HStack } from '@/components/ui/stack'
import { Tooltip } from '@/components/ui/tooltip'
import { Text } from '@/components/ui/typography'
import { IApprovalDetailChildItem } from '@/types/tasksApprovals'
import { formatDate } from '@/utils/date'

import ApproveRejectButtons from './ApproveRejectButtons'

const Container = ({
    item,
    onApprove,
    onReject,
    setRejectReason,
    isRejectAll,
    rejectReason,
}: {
    item: IApprovalDetailChildItem
    onApprove: (entityId: string) => void
    onReject: (entityId: string) => void
    setRejectReason: (reason: string, entityId: string) => void
    isRejectAll: boolean
    rejectReason: string
}) => {
    const [showDetails, setShowDetails] = useState(false)
    const radioOptions: ['reject', 'approve'] = ['reject', 'approve']
    const { t } = useTranslation()

    const handleApproveReject = (value: 'approve' | 'reject') => {
        if (value === 'approve') {
            onApprove(item.approval_entity_id)
        } else if (value === 'reject') {
            onReject(item.approval_entity_id)
        }
    }

    const formatDateOptions = {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
    } as DateTimeFormatOptions

    return (
        <>
            <Flex
                py="8px"
                alignItems={['self-start', 'self-start', 'center', 'center']}
                px="24px"
                flexDir={['column', 'column', 'row', 'row']}
                width="calc(100%)"
                flexGrow="1"
            >
                <Flex
                    width={['calc(100%)', 'calc(100%)', 'calc(70%)', 'calc(70%)']}
                    gap="2%"
                    fontSize="14px"
                    justifyContent="space-between"
                >
                    <Box width="100%" maxW={['calc(33%)', 'calc(33%)', 'calc(33%)', 'calc(33%)']}>
                        <Tooltip content={item.metadata.technicalName}>
                            <Text width="100%" className="ellipsis" fontWeight="bold">
                                {item.metadata.technicalName}
                            </Text>
                        </Tooltip>
                    </Box>
                    <Box width="100%" maxW={['calc(37%)', 'calc(37%)', 'calc(37%)', 'calc(37%)']}>
                        <Tooltip content={item.description}>
                            <Text className="ellipsis" fontWeight="bold">
                                {item.description}
                            </Text>
                        </Tooltip>
                    </Box>
                    <Box width="100%" maxW={['calc(13%)', 'calc(13%)', 'calc(13%)', 'calc(13%)']}>
                        <Tooltip content={item?.metadata?.action}>
                            <Text className="ellipsis" fontWeight="bold">
                                {item?.metadata?.action}
                            </Text>
                        </Tooltip>
                    </Box>
                    <Box
                        width="100%"
                        maxW={['calc(8%)', 'calc(8%)', 'calc(8%)', 'calc(8%)']}
                        onClick={() => setShowDetails(!showDetails)}
                        cursor="pointer"
                    >
                        <Icon path={showDetails ? mdiChevronUp : mdiChevronDown} size="24px" />
                    </Box>
                </Flex>
                <Flex
                    flexGrow="1"
                    justifyContent={['flex-end', 'flex-end', 'space-around', 'space-around']}
                    pt={['8px', '8px', '0', '0']}
                    width={['calc(100%)', 'calc(100%)', 'calc(30%)', 'calc(30%)']}
                >
                    <HStack>
                        {radioOptions.map((value) => {
                            return <ApproveRejectButtons item={item} onClick={handleApproveReject} key={value} value={value} />
                        })}
                    </HStack>
                </Flex>
            </Flex>
            {item.isRejected && !isRejectAll && (
                <Box my="12px" px="24px">
                    <Textarea
                        variant="outline"
                        placeholder="Reason for rejection *"
                        style={{ caretColor: 'var(--chakra-colors-orange-400)' }}
                        value={rejectReason}
                        py="18px"
                        rows={1}
                        onChange={(e) => setRejectReason(e.currentTarget.value, item.approval_entity_id)}
                    />
                </Box>
            )}
            {showDetails && (
                <Box backgroundColor="rgba(84, 79, 64, 0.08)" px="24px" py="16px" my="8px" fontSize="12px">
                    <Flex flexDir={['row', 'row', 'initial', 'initial']} flexWrap={['wrap', 'wrap', 'inherit', 'inherit']} gap="4%">
                        <Flex
                            flexDir={['row', 'row', 'column', 'column']}
                            maxW={['100%', '100%', '48%', '48%']}
                            minW={['100%', '100%', '48%', '48%']}
                            fontSize="12px"
                            flexWrap={['wrap', 'wrap', 'inherit', 'inherit']}
                        >
                            <Text width={['100%', '100%', 'initial', 'initial']}>
                                {t('tasksApprovals.sapGrcAccessRequestDrawer.body.childItems.type')}: {item.metadata.type}
                            </Text>
                            {item.metadata.environment && (
                                <Text width={['100%', '100%', 'initial', 'initial']} pt="8px">
                                    {t('tasksApprovals.sapGrcAccessRequestDrawer.body.childItems.environment')}: {item.metadata.environment}
                                </Text>
                            )}
                            {item.metadata.systemName && (
                                <Text width={['100%', '100%', 'initial', 'initial']} pt="8px">
                                    {t('tasksApprovals.sapGrcAccessRequestDrawer.body.childItems.systemName')}: {item.metadata.systemName}
                                </Text>
                            )}
                            {item.metadata?.businessProcess && (
                                <Text width={['100%', '100%', 'initial', 'initial']} pt="8px">
                                    {t('tasksApprovals.sapGrcAccessRequestDrawer.body.childItems.businessProcess')}:{' '}
                                    {item.metadata.businessProcess}
                                </Text>
                            )}
                        </Flex>
                        <Flex flexDir={['row', 'row', 'column', 'column']} fontSize="12px" flexWrap={['wrap', 'wrap', 'wrap', 'inherit']}>
                            {item.metadata.connector && (
                                <Text width={['100%', '100%', 'initial', 'initial']} pt={['8px', '8px', '0', '0']}>
                                    {t('tasksApprovals.sapGrcAccessRequestDrawer.body.childItems.connector')}: {item.metadata.connector}
                                </Text>
                            )}
                            <Text width={['100%', '100%', 'initial', 'initial']} pt={item.metadata.connector ? '8px' : ''}>
                                {t('tasksApprovals.sapGrcAccessRequestDrawer.body.childItems.validity')}:{' '}
                                {formatDate(item.approval_requested_at, formatDateOptions)} -{' '}
                                {formatDate(item.approval_due_at, formatDateOptions)}
                            </Text>
                            {item.metadata?.businessSubprocess && (
                                <Text width={['100%', '100%', 'initial', 'initial']} pt="8px">
                                    {t('tasksApprovals.sapGrcAccessRequestDrawer.body.childItems.businessSubprocess')}:{' '}
                                    {item.metadata.businessSubprocess}
                                </Text>
                            )}
                        </Flex>
                    </Flex>
                    {item.metadata?.longDescription && (
                        <Text width={'100%'} pt="8px">
                            {t('tasksApprovals.sapGrcAccessRequestDrawer.body.childItems.detailedDescription')}:{' '}
                            {item.metadata.longDescription}
                        </Text>
                    )}
                </Box>
            )}
        </>
    )
}

export default Container
